import React, { useState } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { WhatsAppBotDemo } from '../components/demos/WhatsAppBotDemo';
import { SEOBlogAutomationDemo } from '../components/demos/SEOBlogAutomationDemo';
import {
  ServiceHero,
  AnimatedBreadcrumb,
  ProcessStepper,
  UseCasesAccordion,
  // TechStackGrid, // Hidden for now
  // RelatedServicesCarousel, // Hidden for now
  StickyBookBar,
} from '../components/services';
import {
  GuaranteeSection,
  CTASection,
} from '../components/landing';

// ============================================
// AI Implementation Demo Section
// ============================================
const AIImplementationDemoSection: React.FC = () => {
  const [activeDemo, setActiveDemo] = useState<'whatsapp' | 'seo'>('whatsapp');

  const demos = {
    whatsapp: {
      id: 'whatsapp',
      title: 'AI Customer Support Bot',
      description: 'WhatsApp bot with intent classification, smart routing, and automated responses for real estate inquiries.',
      stats: [
        { value: '92%', label: 'Queries automated' },
        { value: '1.2s', label: 'Avg response time' },
        { value: '3 weeks', label: 'To production' },
      ],
      component: WhatsAppBotDemo,
    },
    seo: {
      id: 'seo',
      title: 'SEO Blog Automation',
      description: 'Automated content pipeline that researches, writes, optimizes, and publishes SEO blog posts to WordPress.',
      stats: [
        { value: '47', label: 'Posts published' },
        { value: '89%', label: 'Avg SEO score' },
        { value: '12', label: 'Ranking page 1' },
      ],
      component: SEOBlogAutomationDemo,
    },
  };

  const currentDemo = demos[activeDemo];
  const DemoComponent = currentDemo.component;

  return (
    <section className="relative py-16 sm:py-20 md:py-24 bg-[#0a0a0a] overflow-hidden">
      {/* Background effects */}
      <motion.div
        className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-blue-500/5 rounded-full blur-[150px]"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Top border */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-blue-500/10 border border-blue-500/20"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
            <span className="text-blue-400 text-sm font-medium">Live Demos</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            See What We Build
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-zinc-400 max-w-2xl mx-auto"
          >
            Interactive demos of real AI systems we've deployed. Not mockups — actual working implementations.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25 }}
            className="text-sm text-zinc-500 mt-3"
          >
            * Simplified demos for showcase purposes. Production systems include full API integrations, databases, and enterprise security.
          </motion.p>
        </div>

        {/* Demo Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.25 }}
          className="flex justify-center gap-3 mb-8"
        >
          {Object.values(demos).map((demo) => (
            <button
              key={demo.id}
              onClick={() => setActiveDemo(demo.id as 'whatsapp' | 'seo')}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                activeDemo === demo.id
                  ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                  : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800 hover:text-white'
              }`}
            >
              {demo.title}
            </button>
          ))}
        </motion.div>

        {/* Demo Description */}
        <AnimatePresence mode="wait">
          <motion.p
            key={activeDemo}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="text-center text-zinc-400 mb-8 max-w-2xl mx-auto"
          >
            {currentDemo.description}
          </motion.p>
        </AnimatePresence>

        {/* Demo Component */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeDemo}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <DemoComponent />
          </motion.div>
        </AnimatePresence>

        {/* Project Stats */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeDemo}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-12 grid md:grid-cols-3 gap-6"
          >
            {currentDemo.stats.map((stat, i) => (
              <div key={i} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
                <div className="text-2xl font-bold text-blue-400 mb-1">{stat.value}</div>
                <div className="text-sm text-zinc-400">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

// ============================================
// Enterprise AI Graveyard Component
// ============================================
const EnterpriseAIGraveyard: React.FC = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const graveyardItems = [
    {
      title: 'The POC that never made it to production',
      description:
        "Six months ago, your team built a proof of concept. It worked in a Jupyter notebook. Leadership was excited. Then someone asked about authentication, data pipelines, error handling, and integration with your CRM. The project is still 'in progress.' Nobody talks about it anymore.",
    },
    {
      title: 'The $200K AI project that delivered a dashboard nobody uses',
      description:
        "You hired a vendor. They promised transformation. Eight months and $200K later, you got a dashboard with impressive charts and a model that works 70% of the time. Not good enough for production. Not bad enough to kill. Just... sitting there.",
    },
    {
      title: "The 'AI strategy' from a big consultancy that's sitting in a drawer",
      description:
        "McKinsey or Accenture came in. Smart people. Nice suits. Four months later, you had a 150-page deck with a 'comprehensive AI roadmap.' Estimated implementation timeline: 18-24 months. The deck is in a shared folder. You haven't opened it since the final presentation.",
    },
    {
      title: 'The internal ML team that spent 6 months on a model with 62% accuracy',
      description:
        "You hired data scientists. Good ones, expensive ones. They spent half a year building a model that technically works but isn't accurate enough to deploy. Now they're iterating. And iterating. Meanwhile, you're paying $400K+ in salaries for a system that hasn't generated a dollar of value.",
    },
    {
      title: "The vendor who promised 'turnkey AI' and delivered a demo that broke on real data",
      description:
        "The demo was flawless. Perfect data, perfect results. Then you fed it your actual data — messy, incomplete, edge cases everywhere. The system collapsed. Three months of 'fixes' later, it still doesn't work reliably.",
    },
  ];

  return (
    <section className="relative py-16 sm:py-20 md:py-24 overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-red-500/5 via-transparent to-transparent" />
      <motion.div
        className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-red-500/5 rounded-full blur-[150px]"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-red-500/10 border border-red-500/20"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            <span className="text-red-400 text-sm font-medium">The Reality</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
          >
            The Enterprise AI Graveyard
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-zinc-400 max-w-2xl mx-auto"
          >
            Let's talk about what's probably sitting in your company right now.
          </motion.p>
        </div>

        {/* Graveyard items as accordion */}
        <div className="space-y-4 mb-16">
          {graveyardItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="rounded-2xl bg-zinc-900/50 border-l-4 border-red-500/50 hover:border-red-500 transition-colors overflow-hidden"
            >
              <button
                onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                className="w-full p-6 text-left flex items-center justify-between gap-4"
              >
                <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                <motion.div
                  animate={{ rotate: expandedIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0 w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center"
                >
                  <svg className="w-4 h-4 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </motion.div>
              </button>
              <AnimatePresence>
                {expandedIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className="px-6 pb-6 text-zinc-400 leading-relaxed">{item.description}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Bottom message */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative"
        >
          <div className="absolute -inset-[1px] bg-gradient-to-r from-blue-500/30 via-violet-500/30 to-blue-500/30 rounded-2xl" />
          <div className="relative bg-zinc-900/90 backdrop-blur-sm rounded-2xl p-8 text-center">
            <p className="text-xl font-semibold text-white mb-4">
              If you've experienced any of these, you're not bad at AI.
            </p>
            <p className="text-lg text-zinc-400 mb-6">
              You just haven't had the right team building it.
            </p>
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6">
              <p className="text-zinc-300">
                <span className="text-white font-semibold">The average failed enterprise AI project costs $250K in direct spend and 2,800 hours of engineering time.</span>{' '}
                That's not counting the opportunity cost of what your team{' '}
                <em>could</em> have been building. Or the credibility hit when
                leadership stops believing AI will ever work at your company.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// Why Projects Fail Section
// ============================================
const WhyProjectsFailSection: React.FC = () => {
  const reasons = [
    {
      number: '01',
      title: 'They start with the model instead of the business problem',
      description:
        '"We should use GPT-4 for something." These are technology decisions. Not business decisions. They lead to solutions looking for problems — the most expensive kind.',
      solution: 'We start with the workflow: Which specific process costs you the most? Which decision is made manually that shouldn\'t be?',
    },
    {
      number: '02',
      title: 'They treat AI as a standalone system',
      description:
        "Most AI projects build beautiful, isolated models that require humans to copy-paste data in and manually move results out. That's not automation. That's a science project.",
      solution: 'We build AI that lives inside your existing systems — integrated with your CRM, your ERP, your databases.',
    },
    {
      number: '03',
      title: 'They optimize for accuracy instead of business impact',
      description:
        "Data science teams love to chase accuracy metrics. 95%. 97%. 99%. But what if 92% accuracy solves 100% of the business problem?",
      solution: 'We optimize for ROI, not vanity metrics. If a model delivers $500K in value at 90% accuracy, we ship it.',
    },
    {
      number: '04',
      title: "They don't have a production deployment plan from Day 1",
      description:
        "Most AI projects are built by data scientists who don't know how to deploy enterprise software.",
      solution: 'We architect for production from the first day — error handling, logging, monitoring, failover, and integration hooks. Not as an afterthought. As the foundation.',
    },
  ];

  return (
    <section className="relative py-16 sm:py-20 md:py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-500/5 to-transparent" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-blue-500/10 border border-blue-500/20"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
            <span className="text-blue-400 text-sm font-medium">The Analysis</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
          >
            The 4 Reasons 87% of Enterprise AI Projects Never Reach Production
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-zinc-400 max-w-3xl mx-auto"
          >
            After deploying 127 AI systems across 23 industries, we've identified exactly why most enterprise AI projects die before they launch.
          </motion.p>
        </div>

        {/* Reasons grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="relative p-8 rounded-2xl bg-zinc-900/50 border border-zinc-800/50 hover:border-blue-500/30 transition-colors group"
            >
              {/* Large faded number */}
              <span className="absolute -top-4 -right-2 text-[120px] font-bold text-zinc-800/30 select-none pointer-events-none group-hover:text-blue-500/10 transition-colors">
                {reason.number}
              </span>

              <div className="relative">
                <h3 className="text-xl font-bold text-white mb-4 pr-16">{reason.title}</h3>
                <p className="text-zinc-400 mb-4 leading-relaxed">{reason.description}</p>
                <div className="pt-4 border-t border-zinc-800/50">
                  <p className="text-blue-400 font-medium">{reason.solution}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Transition text */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center text-xl text-zinc-300 mt-16"
        >
          That's why we created{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400 font-bold">
            The Rapid Deployment Framework™
          </span>
        </motion.p>
      </div>
    </section>
  );
};

// ============================================
// Authority Section
// ============================================

// Icon components for Authority Section
const XMarkIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const CheckMarkIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const DocumentIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

const BeakerIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
  </svg>
);

const RocketLaunchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
  </svg>
);

const AuthoritySection: React.FC = () => {
  const sectionRef = React.useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const items = [
    {
      type: 'not',
      icon: DocumentIcon,
      title: "We're NOT a consulting firm",
      points: [
        'No 6-month discovery phases',
        'No 150-page strategy decks',
        'No junior analysts',
      ],
    },
    {
      type: 'not',
      icon: BeakerIcon,
      title: "We're NOT an AI research lab",
      points: [
        'No chasing state-of-the-art',
        'No publishing papers',
        'No experiments that might work',
      ],
    },
    {
      type: 'is',
      icon: RocketLaunchIcon,
      title: 'We ARE a deployment team',
      points: [
        'We build AI that ships',
        'We integrate into your stack',
        'We get it generating ROI',
      ],
    },
  ];

  return (
    <section ref={sectionRef} className="relative py-16 sm:py-20 md:py-24 bg-[#0a0a0a] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/30 via-transparent to-zinc-900/30" />
      <div className="absolute inset-0 opacity-[0.02]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-800/50 border border-zinc-700/30 mb-6"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
            <span className="text-zinc-400 text-sm">Our Approach</span>
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
            We Don't Pitch. We Build.<br />
            <span className="text-zinc-400">We Don't Consult. We Ship.</span>
          </h2>
        </motion.div>

        {/* Cards grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {items.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              className="relative group"
            >
              {item.type === 'is' && (
                <div className="absolute -inset-[1px] bg-gradient-to-b from-blue-500/30 to-transparent rounded-2xl" />
              )}

              <div className={`relative h-full rounded-2xl p-6 ${
                item.type === 'is'
                  ? 'bg-blue-500/5 border border-blue-500/20'
                  : 'bg-zinc-900/50 border border-zinc-800/50'
              }`}>
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${
                  item.type === 'is'
                    ? 'bg-blue-500/10 border border-blue-500/20'
                    : 'bg-zinc-800/50 border border-zinc-700/30'
                }`}>
                  <item.icon className={`w-6 h-6 ${
                    item.type === 'is' ? 'text-blue-400' : 'text-zinc-500'
                  }`} />
                </div>

                {/* Title */}
                <h3 className={`text-lg font-semibold mb-4 ${
                  item.type === 'is' ? 'text-blue-400' : 'text-white'
                }`}>
                  {item.title}
                </h3>

                {/* Points */}
                <ul className="space-y-3">
                  {item.points.map((point, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
                        item.type === 'is'
                          ? 'bg-blue-500/10'
                          : 'bg-zinc-800/50'
                      }`}>
                        {item.type === 'is' ? (
                          <CheckMarkIcon className="w-3 h-3 text-blue-400" />
                        ) : (
                          <XMarkIcon className="w-3 h-3 text-zinc-500" />
                        )}
                      </div>
                      <span className={`text-sm ${
                        item.type === 'is' ? 'text-zinc-300' : 'text-zinc-500'
                      }`}>
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="bg-zinc-900/50 backdrop-blur-sm rounded-2xl border border-zinc-800/50 overflow-hidden"
        >
          {/* Header row */}
          <div className="grid grid-cols-3 border-b border-zinc-800/50">
            <div className="p-5 border-r border-zinc-800/50">
              <span className="text-zinc-500 text-sm">Provider</span>
            </div>
            <div className="p-5 border-r border-zinc-800/50">
              <span className="text-zinc-500 text-sm">Cost</span>
            </div>
            <div className="p-5">
              <span className="text-zinc-500 text-sm">Deliverable</span>
            </div>
          </div>

          {/* McKinsey row */}
          <div className="grid grid-cols-3 border-b border-zinc-800/50">
            <div className="p-5 border-r border-zinc-800/50">
              <span className="text-zinc-400">McKinsey</span>
            </div>
            <div className="p-5 border-r border-zinc-800/50">
              <span className="text-white font-semibold">$500K+</span>
            </div>
            <div className="p-5">
              <span className="text-zinc-500">Strategy deck</span>
            </div>
          </div>

          {/* Accenture row */}
          <div className="grid grid-cols-3 border-b border-zinc-800/50">
            <div className="p-5 border-r border-zinc-800/50">
              <span className="text-zinc-400">Accenture</span>
            </div>
            <div className="p-5 border-r border-zinc-800/50">
              <span className="text-white font-semibold">18 months</span>
            </div>
            <div className="p-5">
              <span className="text-zinc-500">Staffed team</span>
            </div>
          </div>

          {/* Us row - highlighted */}
          <div className="grid grid-cols-3 bg-blue-500/5">
            <div className="p-5 border-r border-blue-500/20">
              <span className="text-blue-400 font-semibold">AIDeploy</span>
            </div>
            <div className="p-5 border-r border-blue-500/20">
              <span className="text-white font-semibold">Fraction of cost</span>
            </div>
            <div className="p-5">
              <span className="text-blue-400 font-semibold">Working system in weeks</span>
            </div>
          </div>
        </motion.div>

        {/* Bottom message */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="mt-8 text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
            </span>
            <span className="text-amber-400 text-sm font-medium">
              Every week you spend "evaluating" is a week your competitors are deploying.
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// Value Stack Section
// ============================================
const ValueStackSection: React.FC = () => {
  const items = [
    { name: 'Problem-First Discovery', description: 'Deep-dive session to identify highest-ROI AI opportunity', value: 'Included' },
    { name: 'Solution Architecture', description: 'Technical blueprint for production deployment', value: 'Included' },
    { name: 'Rapid Prototype Development', description: 'Working prototype in 14 days', value: 'Included' },
    { name: 'Production System Build', description: 'Full enterprise-grade implementation', value: 'Included' },
    { name: 'System Integration', description: 'Connection with your existing tools and data sources', value: 'Included' },
    { name: 'Deployment & Launch', description: 'Production deployment with monitoring setup', value: 'Included' },
    { name: 'Team Training & Documentation', description: 'Complete knowledge transfer to your team', value: 'Included' },
    { name: '30-Day Post-Launch Support', description: 'Monitoring, optimization, and issue resolution', value: 'Included' },
  ];

  return (
    <section className="relative py-16 sm:py-20 md:py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 via-transparent to-transparent" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-blue-500/10 border border-blue-500/20"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
            <span className="text-blue-400 text-sm font-medium">The Investment</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-4xl font-bold text-white"
          >
            Everything Included
          </motion.h2>
        </div>

        {/* Value items */}
        <div className="space-y-4 mb-12">
          {items.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="flex items-center justify-between p-4 rounded-xl bg-zinc-900/50 border border-zinc-800/50 hover:border-blue-500/30 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-medium">{item.name}</p>
                  <p className="text-sm text-zinc-500">{item.description}</p>
                </div>
              </div>
              <span className="text-zinc-400 font-medium">{item.value}</span>
            </motion.div>
          ))}
        </div>

        {/* Total and pricing */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative"
        >
          <div className="absolute -inset-[1px] bg-gradient-to-r from-blue-500/50 via-violet-500/50 to-blue-500/50 rounded-2xl" />
          <div className="relative bg-zinc-900/90 backdrop-blur-sm rounded-2xl p-8 text-center">
            <p className="text-4xl md:text-5xl font-bold text-white mb-3">Scoped to Your Project</p>
            <p className="text-zinc-400 max-w-xl mx-auto mb-8">
              Final scope depends on your company size, the systems we're building, and integrations needed. We quote after a free strategy session.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-500 to-violet-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all"
            >
              Book Your Free Strategy Session
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// ROI Comparison Section
// ============================================

// Additional icons for ROI section
const UsersIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
  </svg>
);

const RocketIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
  </svg>
);

const ChartBarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
  </svg>
);

const ROIComparisonSection: React.FC = () => {
  const sectionRef = React.useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const hiringDrawbacks = [
    '6-12 months to hire',
    'Onboarding & ramp-up time',
    'First mistakes are expensive',
    'Ongoing salaries & benefits',
  ];

  const ourBenefits = [
    'Ships in weeks',
    'Production-ready code',
    'Fully documented',
    'Knowledge transfer included',
  ];

  return (
    <section ref={sectionRef} className="relative py-16 sm:py-20 md:py-24 bg-[#0a0a0a] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-blue-500/5 rounded-full blur-[150px]"
          animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 opacity-[0.02]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
              backgroundSize: '48px 48px',
            }}
          />
        </div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6"
          >
            <ChartBarIcon className="w-4 h-4 text-blue-400" />
            <span className="text-blue-400 text-sm font-medium">ROI Comparison</span>
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Let's Put This In Perspective
          </h2>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            The real cost of building AI capabilities
          </p>
        </motion.div>

        {/* Comparison Cards */}
        <div className="grid lg:grid-cols-2 gap-6 mb-12">
          {/* Hiring Option */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="h-full bg-zinc-900/50 backdrop-blur-sm rounded-2xl border border-zinc-800/50 p-8">
              {/* Header */}
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-zinc-800/50">
                <div className="w-14 h-14 rounded-xl bg-zinc-800/50 border border-zinc-700/30 flex items-center justify-center">
                  <UsersIcon className="w-7 h-7 text-zinc-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Hiring a 3-Person AI Team</h3>
                  <p className="text-zinc-500 text-sm">The traditional approach</p>
                </div>
              </div>

              {/* Price */}
              <div className="text-center mb-6 py-4">
                <p className="text-5xl font-bold text-zinc-400 line-through decoration-red-500/50">$450K+</p>
                <p className="text-xl text-zinc-500 mt-1">per year</p>
              </div>

              {/* Drawbacks */}
              <ul className="space-y-3 mb-6">
                {hiringDrawbacks.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-500/10 flex items-center justify-center">
                      <XMarkIcon className="w-3 h-3 text-red-400" />
                    </div>
                    <span className="text-zinc-500 text-sm">{item}</span>
                  </motion.li>
                ))}
              </ul>

              {/* Bottom note */}
              <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/10">
                <p className="text-zinc-500 text-sm text-center">
                  Total first-year cost: <span className="text-red-400 font-semibold">$500K+</span>
                </p>
              </div>
            </div>
          </motion.div>

          {/* Our Engagement */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative group"
          >
            {/* Highlight border */}
            <div className="absolute -inset-[1px] bg-gradient-to-b from-blue-500/40 to-violet-500/20 rounded-2xl" />

            <div className="relative h-full bg-gradient-to-br from-blue-500/10 to-violet-500/5 rounded-2xl border border-blue-500/20 p-8">
              {/* Recommended badge */}
              <div className="absolute -top-3 right-6">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                  <CheckMarkIcon className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-400 text-xs font-medium">9x Savings</span>
                </div>
              </div>

              {/* Header */}
              <div className="flex items-center gap-4 mb-6 pb-6 border-b border-blue-500/10">
                <div className="w-14 h-14 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                  <RocketIcon className="w-7 h-7 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Our Full Implementation</h3>
                  <p className="text-blue-400/70 text-sm">The smarter approach</p>
                </div>
              </div>

              {/* Price */}
              <div className="text-center mb-6 py-4">
                <p className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400">Scoped to you</p>
                <p className="text-sm text-zinc-400 mt-2">Typically a fraction of the in-house cost</p>
              </div>

              {/* Benefits */}
              <ul className="space-y-3 mb-6">
                {ourBenefits.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.3, delay: 0.4 + index * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/10 flex items-center justify-center">
                      <CheckMarkIcon className="w-3 h-3 text-emerald-400" />
                    </div>
                    <span className="text-zinc-300 text-sm">{item}</span>
                  </motion.li>
                ))}
              </ul>

              {/* Bottom note */}
              <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                <p className="text-zinc-300 text-sm text-center">
                  <span className="text-blue-400 font-semibold">Investment fits your business</span> — with ROI from day one
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Savings summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="grid sm:grid-cols-3 gap-4 mb-8"
        >
          <div className="bg-zinc-900/50 backdrop-blur-sm rounded-xl border border-zinc-800/50 p-6 text-center">
            <p className="text-zinc-500 text-xs uppercase tracking-wide mb-2">You Save</p>
            <p className="text-3xl font-bold text-emerald-400">$400K+</p>
            <p className="text-zinc-500 text-sm mt-1">in year one</p>
          </div>
          <div className="bg-zinc-900/50 backdrop-blur-sm rounded-xl border border-zinc-800/50 p-6 text-center">
            <p className="text-zinc-500 text-xs uppercase tracking-wide mb-2">Time to Value</p>
            <p className="text-3xl font-bold text-blue-400">Weeks</p>
            <p className="text-zinc-500 text-sm mt-1">not 6-12 months</p>
          </div>
          <div className="bg-zinc-900/50 backdrop-blur-sm rounded-xl border border-zinc-800/50 p-6 text-center">
            <p className="text-zinc-500 text-xs uppercase tracking-wide mb-2">Risk</p>
            <p className="text-3xl font-bold text-violet-400">Zero</p>
            <p className="text-zinc-500 text-sm mt-1">guaranteed delivery</p>
          </div>
        </motion.div>

        {/* ROI highlight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="relative"
        >
          <div className="absolute -inset-[1px] bg-gradient-to-r from-blue-500/30 via-violet-500/30 to-blue-500/30 rounded-xl" />
          <div className="relative bg-zinc-900/80 backdrop-blur-sm rounded-xl p-8 text-center">
            <div className="flex items-center justify-center gap-4">
              <ChartBarIcon className="w-8 h-8 text-blue-400" />
              <p className="text-2xl md:text-3xl font-bold text-white">
                Average client ROI:{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400">340% in year one</span>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// Main Page Component
// ============================================
export const AIImplementationPage: React.FC = () => {
  // Process steps for the stepper
  const processSteps = [
    {
      title: 'Problem-First Discovery',
      description: 'We don\'t ask "what AI do you want?" We ask "what\'s the most expensive problem in your business right now?" We identify the exact workflow, process, or decision that AI should handle. Deliverable: A 1-page Problem Brief.',
      duration: 'Week 1',
    },
    {
      title: 'Rapid Prototype',
      description: 'Within 14 days, you have a working prototype. Not a mockup. Not a presentation. A functional system you can test with real scenarios. You see it work. You find the gaps.',
      duration: 'Weeks 2-3',
    },
    {
      title: 'Production Build',
      description: 'This is where most projects fail. This is where we excel. Full production system with authentication, data pipeline integration, error handling, monitoring, logging, alerting, scalability, and security compliance.',
      duration: 'Weeks 4-8',
    },
    {
      title: 'Deploy & Optimize',
      description: 'We deploy to real users. We monitor performance. We iterate based on actual production data. Then we hand off with full documentation and training for your team.',
      duration: 'Weeks 8-12',
    },
  ];

  // Use cases for the accordion
  const useCases = [
    {
      title: 'Workflow Automation: Invoice Processing',
      description: 'Transform 45-minute manual invoice processing into 90-second automated workflows. Our AI reads, validates, categorizes, and routes invoices with 99%+ accuracy.',
      result: '$340K annual savings',
    },
    {
      title: 'Document Analysis: Contract Review',
      description: 'Reduce contract review from 4-6 hours to 8 minutes per contract. AI-powered risk clause detection with automatic flagging of non-standard terms.',
      result: '99.2% accuracy',
    },
    {
      title: 'Custom AI Agents: Sales Qualification',
      description: 'Cut response time from 4 hours to 4 minutes. AI agents that understand your ICP, qualify leads in real-time, and book more qualified meetings.',
      result: '67% more qualified meetings',
    },
    {
      title: 'Predictive Systems: Demand Forecasting',
      description: 'Move from manual estimates to 12-week SKU-level predictions through AI that learns your seasonality, promotions, and market dynamics.',
      result: '23% inventory cost reduction',
    },
    {
      title: 'Data Pipeline Automation: Regulatory Reporting',
      description: 'Replace 48 hours/month of analyst work with automated daily reporting. Zero compliance delays, 100% audit trail.',
      result: 'Zero human hours required',
    },
    {
      title: 'Customer Intelligence: Churn Prediction',
      description: 'Get 30-day advance warning on at-risk accounts by identifying and flagging accounts before they start shopping competitors.',
      result: '42% churn reduction',
    },
  ];

  // Tech stack - Hidden for now
  // const techStack = [
  //   { name: 'OpenAI GPT-4', category: 'LLM' },
  //   { name: 'Claude', category: 'LLM' },
  //   { name: 'LangChain', category: 'Framework' },
  //   { name: 'Python', category: 'Language' },
  //   { name: 'FastAPI', category: 'Backend' },
  //   { name: 'PostgreSQL', category: 'Database' },
  //   { name: 'Redis', category: 'Cache' },
  //   { name: 'Docker', category: 'Infrastructure' },
  //   { name: 'AWS', category: 'Cloud' },
  //   { name: 'Kubernetes', category: 'Orchestration' },
  //   { name: 'Datadog', category: 'Monitoring' },
  //   { name: 'Pinecone', category: 'Vector DB' },
  // ];

  // Related services - hidden for now
  // const relatedServices = [
  //   {
  //     title: 'AI Profit Audit',
  //     description: 'Discover your highest-ROI AI opportunities',
  //     href: '/services/ai-profit-audit',
  //     accent: 'emerald' as const,
  //   },
  //   {
  //     title: 'Vibe Coding',
  //     description: 'Rapid prototyping and MVP development',
  //     href: '/services/vibe-code',
  //     accent: 'violet' as const,
  //   },
  // ];

  return (
    <main className="bg-[#0a0a0a]">
      {/* Sticky Book Bar */}
      <StickyBookBar
        ctaText="Book Your Free Strategy Session"
        ctaHref="/contact"
        accent="blue"
      />

      {/* Breadcrumb */}
      <div className="pt-24 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedBreadcrumb
            items={[
              { label: 'Home', href: '/' },
              { label: 'Services', href: '/services' },
              { label: 'AI Implementation', href: '/services/ai-implementation' },
            ]}
            accent="blue"
          />
        </div>
      </div>

      {/* Hero Section */}
      <ServiceHero
        eyebrow="For CEOs and CTOs who are done paying for AI strategy decks that collect dust"
        title="We Build, Deploy, and Integrate Production AI Systems Into Your Business"
        subtitle="In 4-12 Weeks, Not 4-12 Months"
        description="You've probably tried AI before. A POC that never shipped. A consulting engagement that delivered slides. We're not that. We build working AI systems that integrate directly into your tech stack — and we don't leave until they're generating measurable ROI."
        metrics={[
          { value: '127+', label: 'Systems Deployed' },
          { value: '4-12', label: 'Weeks to Production' },
          { value: '340%', label: 'Avg Year 1 ROI' },
        ]}
        ctaText="Book Your Free Strategy Session"
        ctaHref="/contact"
        accent="blue"
      />

      {/* The Enterprise AI Graveyard */}
      <EnterpriseAIGraveyard />

      {/* Why Most AI Projects Fail */}
      <WhyProjectsFailSection />

      {/* The Rapid Deployment Framework - Process Stepper */}
      <section className="py-16 sm:py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-blue-500/10 border border-blue-500/20"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
              <span className="text-blue-400 text-sm font-medium">Our Process</span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4"
            >
              The Rapid Deployment Framework™
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-xl text-zinc-400"
            >
              How We Build AI That Actually Ships
            </motion.p>
          </div>

          <ProcessStepper
            steps={processSteps}
            accent="blue"
            autoAdvance={true}
            autoAdvanceInterval={5000}
          />

          {/* Bottom statement */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <p className="text-xl text-zinc-300 mb-2">
              At the end of this process, you don't have a proof of concept.
            </p>
            <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-violet-400">
              You have a working AI system in production.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Use Case Showcase */}
      <section className="py-16 sm:py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-blue-500/10 border border-blue-500/20"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
              <span className="text-blue-400 text-sm font-medium">What We Build</span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-white"
            >
              Real Systems. Real Results. Real ROI.
            </motion.h2>
          </div>

          <UseCasesAccordion
            title="Use Cases"
            cases={useCases}
            accent="blue"
          />
        </div>
      </section>

      {/* Live Demo Showcase */}
      <AIImplementationDemoSection />

      {/* Authority Section */}
      <AuthoritySection />

      {/* Value Stack */}
      <ValueStackSection />

      {/* ROI Comparison */}
      <ROIComparisonSection />

      {/* Tech Stack - Hidden for now */}
      {/* <section className="py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-bold text-white"
            >
              Built With Enterprise-Grade Technology
            </motion.h2>
          </div>

          <TechStackGrid
            title="Our Technology Stack"
            items={techStack}
            accent="blue"
          />
        </div>
      </section> */}

      {/* Guarantee Section */}
      <GuaranteeSection
        headline="Our 50% Performance Guarantee"
        guarantee="If the system we build doesn't outperform your current process by at least 50% on the metric we agree on before starting, we'll continue working at no additional cost until it does."
        details={[
          'Before we start, we agree on the success metric: response time, processing cost, accuracy rate, revenue impact.',
          'No weasel words. No scope limitations. No expiration date.',
          'We hit the number or we keep working.',
        ]}
        riskStatement="Clearly defined success metrics agreed upfront"
        upsideStatement="A production AI system with guaranteed 50%+ improvement"
        ctaText="Book Your Free Strategy Session"
        ctaHref="/contact"
      />

      {/* Related Services - Hidden for now */}
      {/* <RelatedServicesCarousel
        title="Explore Related Services"
        services={relatedServices}
      /> */}

      {/* Final CTA Section */}
      <CTASection
        headline="Your Competitors Are Already Building"
        subheadline="Every month you delay, they compound their advantage. The gap between AI leaders and laggards is widening. In 45 minutes, we'll show you exactly how to close it."
        ctaText="Book Your Free Strategy Session"
        ctaHref="/contact"
        urgencyText="We take on 6 projects per quarter. 2 spots left for Q2."
        secondaryText="Free session. No sales pitch. Just a clear path forward."
      />
    </main>
  );
};

export default AIImplementationPage;
